export interface DriverTreatmentItem {
  label: string;
  tier: 'universal' | 'tier1' | 'tier2' | 'advanced' | 'inOffice';
  treatmentIds?: string[];
}

export interface DriverTreatmentGroup {
  driverKey: string;
  driverLabel: string;
  treatments: DriverTreatmentItem[];
}

export const UNIVERSAL_TREATMENTS: DriverTreatmentItem[] = [
  { label: 'Screen breaks, optimized blinking, improved sleep hygiene', tier: 'universal', treatmentIds: ['blinking-exercises'] },
  { label: 'Moisture-retaining spectacles/goggles', tier: 'universal', treatmentIds: ['moisture-chamber'] },
  { label: 'Humidifier / environmental humidity control', tier: 'universal' },
  { label: 'Omega-3 PUFAs (oral)', tier: 'universal', treatmentIds: ['omega-3'] },
  { label: 'Vitamins A, B12 & D supplementation', tier: 'universal' },
  { label: 'Patient education on treatment adherence', tier: 'universal' }
];

export const DRIVER_TREATMENTS: DriverTreatmentGroup[] = [
  {
    driverKey: 'dewsDriverLipid',
    driverLabel: 'Lipid Layer Deficiency / MGD',
    treatments: [
      { label: 'Warm compresses (40\u201341.5\u00b0C, \u226510 min/day) + lid massage', tier: 'tier1', treatmentIds: ['warm-compresses'] },
      { label: 'Lipid-containing artificial tears', tier: 'tier1' },
      { label: 'Lid hygiene wipes/foam', tier: 'tier1' },
      { label: 'Blink exercises', tier: 'tier1', treatmentIds: ['blinking-exercises'] },
      { label: 'Oral omega-3', tier: 'tier1', treatmentIds: ['omega-3'] },
      { label: 'Topical azithromycin (ophthalmic)', tier: 'tier2', treatmentIds: ['azithromycin'] },
      { label: 'Cyclosporine A (Restasis / Cequa / Vevye)', tier: 'tier2', treatmentIds: ['restasis-and-restasis-multidose', 'cequa', 'vevye'] },
      { label: 'IPL (Intense Pulsed Light)', tier: 'inOffice', treatmentIds: ['intense-pulsed-light-therapy-(ipl)'] },
      { label: 'Thermal pulsation \u2014 LipiFlow / TearCare / iLux', tier: 'inOffice', treatmentIds: ['tearcare', 'ilux'] },
      { label: 'LLLT (photobiomodulation)', tier: 'inOffice' },
      { label: 'Lid margin debridement', tier: 'inOffice' },
      { label: 'Microblepharoexfoliation (BlephEx)', tier: 'inOffice', treatmentIds: ['blephex-(microblepharoexfoliation)'] }
    ]
  },
  {
    driverKey: 'dewsDriverAqueous',
    driverLabel: 'Aqueous Deficiency',
    treatments: [
      { label: 'HA-containing artificial tears (0.1\u20130.4%)', tier: 'tier1' },
      { label: 'Trehalose-based drops', tier: 'tier1' },
      { label: 'Moisture-retaining spectacles/goggles', tier: 'tier1', treatmentIds: ['moisture-chamber'] },
      { label: 'Punctal plugs (collagen/silicone) or intracanalicular HA filler', tier: 'tier1', treatmentIds: ['punctal-plugs'] },
      { label: 'Cyclosporine A (Restasis / Cequa / Vevye)', tier: 'tier2', treatmentIds: ['restasis-and-restasis-multidose', 'cequa', 'vevye'] },
      { label: 'Lifitegrast 5% (Xiidra)', tier: 'tier2', treatmentIds: ['xiidra'] },
      { label: 'Varenicline nasal spray (Tyrvaya) \u2014 nicotinic receptor agonist', tier: 'tier2', treatmentIds: ['tyrvaya'] },
      { label: 'Scleral contact lenses', tier: 'advanced', treatmentIds: ['scleral-lenses'] },
      { label: 'Autologous serum / PRP / PRGF drops', tier: 'advanced', treatmentIds: ['autologous-serum'] },
      { label: 'PROKERA cryopreserved amniotic membrane (3\u20137 days)', tier: 'advanced', treatmentIds: ['prokera'] }
    ]
  },
  {
    driverKey: 'dewsDriverMucinGlycocalyx',
    driverLabel: 'Mucin / Glycocalyx Deficiency',
    treatments: [
      { label: 'HP-guar based lubricants', tier: 'tier1' },
      { label: 'Diquafosol 3% (P2Y2 agonist \u2014 available in Asia)', tier: 'tier1' },
      { label: 'Cyclosporine A', tier: 'tier2', treatmentIds: ['restasis-and-restasis-multidose', 'cequa', 'vevye'] },
      { label: 'Lifitegrast 5% (Xiidra)', tier: 'tier2', treatmentIds: ['xiidra'] },
      { label: 'Rebamipide 2% (mucin secretagogue \u2014 available in Asia)', tier: 'tier2' }
    ]
  },
  {
    driverKey: 'dewsDriverBlinkMechanism',
    driverLabel: 'Blink Mechanism Abnormality',
    treatments: [
      { label: 'Blink exercises \u2014 forceful complete blinks', tier: 'tier1', treatmentIds: ['blinking-exercises'] },
      { label: 'Blink reminder software (screen-use biofeedback)', tier: 'tier1' },
      { label: 'Moisture-retaining spectacles/goggles', tier: 'tier1', treatmentIds: ['moisture-chamber'] }
    ]
  },
  {
    driverKey: 'dewsDriverLidMargin',
    driverLabel: 'Lid Margin Disease / Blepharitis',
    treatments: [
      { label: 'Lid hygiene wipes/foam (TTO, capryloyl glycine, aloe vera)', tier: 'tier1', treatmentIds: ['blephadex'] },
      { label: 'Hypochlorous acid 0.01% spray', tier: 'tier1', treatmentIds: ['avenova'] },
      { label: 'Lotilaner 0.25% (XDEMVY) \u2014 if Demodex/collarettes present', tier: 'tier2', treatmentIds: ['xdemvy'] },
      { label: 'Tea tree oil wipes (terpinen-4-ol) \u2014 for Demodex', tier: 'tier2', treatmentIds: ['blephadex'] },
      { label: 'Topical azithromycin \u2014 for bacterial blepharitis', tier: 'tier2', treatmentIds: ['azithromycin'] },
      { label: 'Oral doxycycline 50\u2013100 mg/day \u2014 moderate/severe or rosacea', tier: 'tier2', treatmentIds: ['doxycycline'] },
      { label: 'Microblepharoexfoliation (BlephEx)', tier: 'inOffice', treatmentIds: ['blephex-(microblepharoexfoliation)'] },
      { label: 'IPL \u2014 especially for rosacea-associated lid margin disease', tier: 'inOffice', treatmentIds: ['intense-pulsed-light-therapy-(ipl)'] }
    ]
  },
  {
    driverKey: 'dewsDriverAnatomicalAlignment',
    driverLabel: 'Anatomical Alignment Issue',
    treatments: [
      { label: 'HA lubricants for symptomatic relief', tier: 'tier1' },
      { label: 'Moisture-retaining goggles (lagophthalmos)', tier: 'tier1', treatmentIds: ['moisture-chamber'] },
      { label: 'Lubricating ointment at night (nocturnal lagophthalmos)', tier: 'tier1' },
      { label: 'Surgical referral \u2014 conjunctivochalasis excision, pterygium, entropion/ectropion', tier: 'advanced' }
    ]
  },
  {
    driverKey: 'dewsDriverNeuralDysfunction',
    driverLabel: 'Neural Dysfunction',
    treatments: [
      { label: 'Vitamin A/B12 supplements', tier: 'tier1' },
      { label: 'Scleral contact lenses \u2014 reduce mechanical nerve stimulation', tier: 'tier2', treatmentIds: ['scleral-lenses'] },
      { label: 'Cenegermin 0.002% (Oxervate) \u2014 recombinant NGF', tier: 'tier2', treatmentIds: ['oxervate'] },
      { label: 'Autologous serum / PRP / PRGF drops (contain NGF)', tier: 'advanced', treatmentIds: ['autologous-serum'] },
      { label: 'IPL \u2014 shown benefit in neuropathic DED/photophobia', tier: 'inOffice', treatmentIds: ['intense-pulsed-light-therapy-(ipl)'] },
      { label: 'Transcutaneous electrical nerve stimulation', tier: 'inOffice' }
    ]
  },
  {
    driverKey: 'dewsDriverCellDamage',
    driverLabel: 'Ocular Surface Cell Damage / Disruption',
    treatments: [
      { label: 'HA-containing tears (high molecular weight)', tier: 'tier1' },
      { label: 'Trehalose-based drops', tier: 'tier1' },
      { label: 'Perfluorohexyloctane (Miebo) \u2014 FDA-approved 2023, reduces evaporation', tier: 'tier2', treatmentIds: ['miebo'] },
      { label: 'Autologous serum / PRP / PRGF drops', tier: 'advanced', treatmentIds: ['autologous-serum'] },
      { label: 'PROKERA cryopreserved amniotic membrane', tier: 'advanced', treatmentIds: ['prokera'] },
      { label: 'Scleral contact lenses', tier: 'advanced', treatmentIds: ['scleral-lenses'] },
      { label: 'Bandage contact lenses', tier: 'advanced' }
    ]
  },
  {
    driverKey: 'dewsDriverInflammation',
    driverLabel: 'Inflammation / Oxidative Stress',
    treatments: [
      { label: 'Omega-3 PUFAs (oral)', tier: 'tier1', treatmentIds: ['omega-3'] },
      { label: 'Vitamin D supplementation', tier: 'tier1' },
      { label: 'Short-term loteprednol 0.25% (EYSUVIS) \u2014 max 2 weeks for flares', tier: 'tier2', treatmentIds: ['eysuvis'] },
      { label: 'Cyclosporine A (Restasis / Cequa / Vevye)', tier: 'tier2', treatmentIds: ['restasis-and-restasis-multidose', 'cequa', 'vevye'] },
      { label: 'Lifitegrast 5% (Xiidra)', tier: 'tier2', treatmentIds: ['xiidra'] },
      { label: 'IPL \u2014 reduces tear cytokines (IL-1\u03b2, IL-6, IL-13)', tier: 'inOffice', treatmentIds: ['intense-pulsed-light-therapy-(ipl)'] },
      { label: 'Autologous serum / PRP / PRGF drops', tier: 'advanced', treatmentIds: ['autologous-serum'] }
    ]
  }
];
